import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useAuthStore } from '@/store/auth/auth.store';
import {
  AlertSeverityEnum,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import {
  ITicketArchivo,
  NivelAfectacion,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

import {
  USE_MOCK,
  MOCK_MASIVOS,
  MOCK_MASIVOS_PAGING,
  MOCK_CLIENTES_BUSQUEDA,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, patch } = erpApi();

export type PausarMasivoParams = { id: string; motivo: string };
export type ReactivarMasivoParams = { id: string; comentario?: string };
export type AgregarComentarioMasivoParams = { id: string; comentario: string };

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

///* tanStack query

export type GetMasivosParams = {
  page?: number;
  page_size?: number;
  estado?: string;
  area?: string;
  enabled?: boolean;
};

export type ResolverMasivoParams = {
  id: string;
  data: Record<string, unknown>;
};

export const useFetchMasivos = ({
  page = 1,
  page_size = 10,
  enabled = true,
  ...params
}: GetMasivosParams) => {
  return useQuery({
    queryKey: ['gestion-tickets-masivos', page, page_size, params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_MASIVOS_PAGING);
      return getMasivos({ page, page_size, ...params });
    },
    enabled,
  });
};

export const useGetMasivo = (id: string) => {
  return useQuery({
    queryKey: ['gestion-tickets-masivos', id],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_MASIVOS.find((m) => m.id_ticket_masivo === id) || MOCK_MASIVOS[0]);
      return getMasivo(id);
    },
  });
};

export const useCreateMasivo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMasivo,
    onSuccess: () => {
      // refetchQueries con type:'active' fuerza el GET inmediato de la lista,
      // detalle, historial, clientes afectados — todo lo que tenga prefijo
      // 'gestion-tickets-masivos' y este montado. Sin esto, invalidateQueries
      // solo marca stale y el usuario tiene que recargar para ver cambios.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      // Las alertas de "MASIVO" en buscador de cliente / vision 360 dependen
      // de queries paralelas — las refrescamos para que reflejen el estado
      // nuevo del masivo (creado / pausado / activo / resuelto).
      queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Masivo creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useResolverMasivo = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: resolverMasivo,
    onSuccess: () => {
      // refetchQueries con type:'active' fuerza el GET inmediato de la lista,
      // detalle, historial, clientes afectados — todo lo que tenga prefijo
      // 'gestion-tickets-masivos' y este montado. Sin esto, invalidateQueries
      // solo marca stale y el usuario tiene que recargar para ver cambios.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      // Las alertas de "MASIVO" en buscador de cliente / vision 360 dependen
      // de queries paralelas — las refrescamos para que reflejen el estado
      // nuevo del masivo (creado / pausado / activo / resuelto).
      queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' });

      setAlert &&
        setAlert(
          true,
          'Masivo resuelto correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const usePausarMasivo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: pausarMasivo,
    onSuccess: () => {
      // refetchQueries con type:'active' fuerza el GET inmediato de la lista,
      // detalle, historial, clientes afectados — todo lo que tenga prefijo
      // 'gestion-tickets-masivos' y este montado. Sin esto, invalidateQueries
      // solo marca stale y el usuario tiene que recargar para ver cambios.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      // Las alertas de "MASIVO" en buscador de cliente / vision 360 dependen
      // de queries paralelas — las refrescamos para que reflejen el estado
      // nuevo del masivo (creado / pausado / activo / resuelto).
      queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' });
      setAlert && setAlert(true, 'Masivo pausado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useReactivarMasivo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: reactivarMasivo,
    onSuccess: () => {
      // refetchQueries con type:'active' fuerza el GET inmediato de la lista,
      // detalle, historial, clientes afectados — todo lo que tenga prefijo
      // 'gestion-tickets-masivos' y este montado. Sin esto, invalidateQueries
      // solo marca stale y el usuario tiene que recargar para ver cambios.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      // Las alertas de "MASIVO" en buscador de cliente / vision 360 dependen
      // de queries paralelas — las refrescamos para que reflejen el estado
      // nuevo del masivo (creado / pausado / activo / resuelto).
      queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' });
      setAlert && setAlert(true, 'Masivo reactivado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useAgregarComentarioMasivo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: agregarComentarioMasivo,
    onSuccess: (_data, variables) => {
      // Refetch del historial especifico (queryKey con variables.id) +
      // refetch general de masivos para que la lista se actualice con la
      // nueva fecha_modificacion / ultimo comentario.
      queryClient.refetchQueries({
        queryKey: ['gestion-tickets-masivos', 'historial', variables.id],
        type: 'active',
      });
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      setAlert && setAlert(true, 'Comentario agregado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useGetHistorialMasivo = (masivoId: string) => {
  return useQuery({
    queryKey: ['gestion-tickets-masivos', 'historial', masivoId],
    queryFn: async () => getHistorialMasivo(masivoId),
    enabled: !!masivoId,
  });
};

export const useFetchClientesAfectados = (
  masivoId: string,
  { page = 1, page_size = 50 }: { page?: number; page_size?: number } = {},
) => {
  return useQuery({
    queryKey: ['gestion-tickets-masivos', 'clientes', masivoId, page, page_size],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_CLIENTES_BUSQUEDA);
      return getClientesAfectados(masivoId, page, page_size);
    },
    enabled: !!masivoId,
  });
};

export const useFetchPreviewClientesMasivo = (
  nivel: NivelAfectacion | '',
  params: { olt?: string; troncal?: string; nap?: string; router?: string } = {},
) => {
  const hasRequiredParam = nivel === 'olt' ? !!params.olt
    : nivel === 'troncal' ? !!params.troncal
    : nivel === 'nap' ? !!params.nap
    : nivel === 'router' ? !!params.router
    : false;

  return useQuery({
    queryKey: ['masivo-preview-clientes', nivel, params],
    queryFn: async () => {
      return getPreviewClientes(nivel, params);
    },
    enabled: !!nivel && hasRequiredParam,
  });
};

export const useFetchInfraestructuraMasivo = (
  nivel: NivelAfectacion | '',
  filtroPadre?: string,
) => {
  return useQuery({
    queryKey: ['masivo-infraestructura', nivel, filtroPadre],
    queryFn: async () => {
      return getInfraestructura(nivel, filtroPadre);
    },
    enabled: !!nivel,
  });
};

/**
 * Variante multi-padre: dado un nivel y una lista de codigos padre,
 * hace N fetches en paralelo y concatena los resultados dedup por codigo.
 * Respeta las rules of hooks (una sola llamada a useQuery).
 */
export const useFetchInfraestructuraMasivoMulti = (
  nivel: NivelAfectacion | '',
  padres: string[],
) => {
  // Ordenar para que el queryKey sea estable frente a cambios de orden
  const padresOrdenados = [...padres].sort();
  const key = padresOrdenados.join(',');

  return useQuery({
    queryKey: ['masivo-infraestructura-multi', nivel, key],
    queryFn: async () => {
      if (!nivel || padresOrdenados.length === 0) return { data: { data: [] } };
      const responses = await Promise.all(
        padresOrdenados.map((p) => getInfraestructura(nivel, p)),
      );
      // dedup por codigo
      const seen = new Set<string>();
      const merged: any[] = [];
      responses.forEach((r: any) => {
        const list: any[] = r?.data?.data ?? r?.data ?? [];
        list.forEach((item) => {
          if (item?.codigo && !seen.has(item.codigo)) {
            seen.add(item.codigo);
            merged.push(item);
          }
        });
      });
      return { data: { data: merged } };
    },
    enabled: !!nivel && padresOrdenados.length > 0,
  });
};

///* axios
export const getMasivos = async (params?: GetMasivosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get(
    `${BASE_URL}masivos/?${queryParams}`,
    true
  );
};

export const getMasivo = async (id: string) => {
  try {
    return await get(
      `${BASE_URL}masivos/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createMasivo = async (data: Record<string, unknown>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post(
    `${BASE_URL}masivos/`,
    data,
    true
  );
};

export const resolverMasivo = async ({
  id,
  data,
}: ResolverMasivoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return patch(
    `${BASE_URL}masivos/${id}/resolver/`,
    data,
    true
  );
};

export const pausarMasivo = async ({ id, motivo }: PausarMasivoParams) => {
  return patch(`${BASE_URL}masivos/${id}/pausar/`, { motivo }, true);
};

export const reactivarMasivo = async ({ id, comentario }: ReactivarMasivoParams) => {
  return patch(`${BASE_URL}masivos/${id}/reactivar/`, { comentario: comentario ?? '' }, true);
};

export const agregarComentarioMasivo = async ({
  id,
  comentario,
}: AgregarComentarioMasivoParams) => {
  return post(`${BASE_URL}masivos/${id}/comentarios/`, { comentario }, true);
};

export const getHistorialMasivo = async (id: string) => {
  try {
    return await get(`${BASE_URL}masivos/${id}/historial/`, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getClientesAfectados = async (
  masivoId: string,
  page = 1,
  pageSize = 50,
) => {
  try {
    return await get(
      `${BASE_URL}masivos/${masivoId}/clientes/?page=${page}&page_size=${pageSize}`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getPreviewClientes = async (
  nivel: string,
  params: { olt?: string; troncal?: string; nap?: string; router?: string } = {},
) => {
  try {
    let url = `${BASE_URL}masivos/preview-clientes/?nivel=${nivel}`;
    if (params.olt) url += `&olt=${params.olt}`;
    if (params.troncal) url += `&troncal=${params.troncal}`;
    if (params.nap) url += `&nap=${params.nap}`;
    if (params.router) url += `&router=${params.router}`;
    return await get(url, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getInfraestructura = async (
  nivel: string,
  filtroPadre?: string,
) => {
  try {
    let url = `${BASE_URL}masivos/infraestructura/?nivel=${nivel}`;
    if (filtroPadre) {
      // El backend espera el codigo del padre via &filtro=...
      // (consistente con MasivoService.obtenerInfraestructura).
      url += `&filtro=${encodeURIComponent(filtroPadre)}`;
    }
    return await get(url, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * Hook async + polling para sync de infraestructura masivos. Mismo patron
 * que useSincronizarContratosErp: POST devuelve {job_id}, despues GET
 * status/{id} cada 1s hasta done=true. Permite que el frontend pinte una
 * barra de progreso real con la fase actual (OLT → Troncal → NAP → Router).
 */
import { useEffect as useEffectInfra, useState as useStateInfra } from 'react';

export type SyncJobStatusInfra = {
  job_id: string;
  tipo: string;
  stage: string;
  total: number;
  processed: number;
  percent: number;
  done: boolean;
  error?: string | null;
  mensajes?: string[];
  resumen?: Record<string, any> | null;
};

export const useSincronizarInfraestructuraMasivos = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const [jobId, setJobId] = useStateInfra<string | null>(null);

  const startMutation = useMutation({
    mutationFn: async () =>
      post<{ job_id: string }>(
        `${BASE_URL}masivos/infraestructura/sincronizar-erp`,
        {},
        true,
      ),
    onSuccess: (res: any) => {
      const id = res?.data?.job_id;
      if (id) setJobId(id);
    },
    onError: (error) => handleAxiosError(error),
  });

  const statusQuery = useQuery({
    queryKey: ['sync-infra-status', jobId],
    queryFn: async () =>
      get<SyncJobStatusInfra>(
        `${BASE_URL}masivos/infraestructura/sincronizar-erp/status/${jobId}`,
        true,
      ),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data: any = query.state.data;
      const status = data?.data;
      if (status?.done) return false;
      return 1000;
    },
    refetchOnWindowFocus: false,
  });

  const status: SyncJobStatusInfra | null =
    ((statusQuery.data as any)?.data as SyncJobStatusInfra) ?? null;

  useEffectInfra(() => {
    if (status?.done && !status.error) {
      queryClient.invalidateQueries({ queryKey: ['masivo-infraestructura'] });
      queryClient.invalidateQueries({ queryKey: ['masivo-infraestructura-multi'] });
      const r = (status.resumen ?? {}) as any;
      const partes: string[] = [];
      ['olts', 'troncales', 'naps', 'routers'].forEach((k) => {
        const v = r?.[k];
        if (v && typeof v === 'object') {
          partes.push(`${k}: ${v.nuevos ?? 0} nuevos, ${v.actualizados ?? 0} actualizados`);
        }
      });
      const msg = partes.length
        ? `Sync infra OK. ${partes.join(' | ')}`
        : 'Sync de infraestructura completada.';
      setAlert?.(true, msg, AlertSeverityEnum.success);
    }
  }, [status?.done]);

  return {
    start: () => startMutation.mutate(),
    isStarting: startMutation.isPending,
    jobId,
    status,
    isActive: !!jobId && !status?.done,
    resetJob: () => setJobId(null),
  };
};

// ============================================================================
// CREAR MASIVO CON ARCHIVOS — endpoint multipart
// ============================================================================

type CreateMasivoConArchivosParams = {
  data: Record<string, unknown>; // payload JSON del masivo (mismos campos que createMasivo)
  archivos: File[];
};

/**
 * Crea el masivo via multipart con N archivos. El payload del masivo se
 * envia en el part "masivo" como JSON serializado (Spring lo deserializa
 * a TicketMasivo en el backend).
 */
export const createMasivoConArchivos = async (
  { data, archivos }: CreateMasivoConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  fd.append('masivo', new Blob([JSON.stringify(data)], { type: 'application/json' }));
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}masivos/crear-con-archivos`,
    fd,
    { headers: { Authorization: 'Token ' + token }, timeout: 600000 },
  );
  return resp.data;
};

export const useCreateMasivoConArchivos = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMasivoConArchivos,
    onSuccess: () => {
      // refetchQueries con type:'active' fuerza el GET inmediato de la lista,
      // detalle, historial, clientes afectados — todo lo que tenga prefijo
      // 'gestion-tickets-masivos' y este montado. Sin esto, invalidateQueries
      // solo marca stale y el usuario tiene que recargar para ver cambios.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' });
      // Las alertas de "MASIVO" en buscador de cliente / vision 360 dependen
      // de queries paralelas — las refrescamos para que reflejen el estado
      // nuevo del masivo (creado / pausado / activo / resuelto).
      queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert && setAlert
        && setAlert(true, customSuccessMessage ?? 'Masivo creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

// ============================================================================
// ADJUNTOS — endpoints multipart sobre el masivo
// ============================================================================
// Comentario / pausa / resolucion con archivos. Reusan el endpoint clasico
// JSON cuando no hay archivos (las funciones decisorias quedan en el
// componente del frontend), o llaman al endpoint multipart cuando si los
// hay. Mantenemos ambas variantes para no romper consumidores existentes.

type AgregarComentarioMasivoConArchivosParams = {
  id: string;
  comentario: string;
  archivos: File[];
};

export const agregarComentarioMasivoConArchivos = async (
  { id, comentario, archivos }: AgregarComentarioMasivoConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  if (comentario && comentario.trim().length > 0) {
    fd.append('comentario', new Blob([comentario], { type: 'text/plain' }));
  }
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}masivos/${id}/comentarios-con-archivos`,
    fd,
    { headers: { Authorization: 'Token ' + token }, timeout: 600000 },
  );
  return resp.data;
};

export const useAgregarComentarioMasivoConArchivos = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: agregarComentarioMasivoConArchivos,
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'historial', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'archivos', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', vars.id], type: 'active' }),
        // Lista + clientes afectados + alertas en busqueda — todo refetch.
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' }),
      ]);
      setAlert && setAlert(true, 'Comentario agregado', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

type PausarMasivoConArchivosParams = { id: string; motivo: string; archivos: File[] };

export const pausarMasivoConArchivos = async (
  { id, motivo, archivos }: PausarMasivoConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  fd.append('motivo', new Blob([motivo], { type: 'text/plain' }));
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}masivos/${id}/pausar-con-archivos`,
    fd,
    { headers: { Authorization: 'Token ' + token }, timeout: 600000 },
  );
  return resp.data;
};

export const usePausarMasivoConArchivos = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: pausarMasivoConArchivos,
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'historial', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'archivos', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', vars.id], type: 'active' }),
        // Lista + clientes afectados + alertas en busqueda — todo refetch.
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets-masivos'] }),
      ]);
      setAlert && setAlert(true, 'Masivo pausado', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

type ResolverMasivoConArchivosParams = {
  id: string;
  observacion: string;
  subcategoriaCierreId?: string | null;
  archivos: File[];
};

export const resolverMasivoConArchivos = async (
  { id, observacion, subcategoriaCierreId, archivos }: ResolverMasivoConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  fd.append('observacion_resolucion', new Blob([observacion], { type: 'text/plain' }));
  if (subcategoriaCierreId) {
    fd.append('subcategoria_cierre_id', new Blob([subcategoriaCierreId], { type: 'text/plain' }));
  }
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}masivos/${id}/resolver-con-archivos`,
    fd,
    { headers: { Authorization: 'Token ' + token }, timeout: 600000 },
  );
  return resp.data;
};

export const useResolverMasivoConArchivos = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: resolverMasivoConArchivos,
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'historial', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', 'archivos', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos', vars.id], type: 'active' }),
        // Lista + clientes afectados + alertas en busqueda — todo refetch.
        queryClient.refetchQueries({ queryKey: ['gestion-tickets-masivos'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['verificar-masivo'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['buscar-clientes'], type: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets-masivos'] }),
      ]);
      setAlert && setAlert(true, 'Masivo resuelto', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

// Listado de archivos del masivo + helper para construir la URL de descarga.

export const getMasivoArchivos = async (masivoId: string) => {
  return get<ITicketArchivo[]>(`${BASE_URL}masivos/${masivoId}/archivos/`, true);
};

export const useFetchMasivoArchivos = (masivoId: string) => {
  return useQuery({
    queryKey: ['gestion-tickets-masivos', 'archivos', masivoId],
    queryFn: () => getMasivoArchivos(masivoId),
    enabled: !!masivoId,
  });
};

/**
 * Pide al backend la URL firmada de MinIO (15 min) para el archivo del
 * masivo y la abre en una nueva pestaña. Mismo motivo que en
 * descargarArchivo de tickets: anchor <a href> no envia Authorization
 * en una pestaña nueva y el gateway rechaza con 401.
 */
export const descargarArchivoMasivo = async (archivoId: string) => {
  try {
    const resp = await get<{ url: string; nombre_original?: string; content_type?: string }>(
      `${BASE_URL}masivos/archivos/${archivoId}/download`,
      true,
    );
    const url = (resp as any)?.data?.url;
    if (!url) {
      throw new Error('Respuesta sin URL de descarga');
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    handleAxiosError(error);
  }
};
